import { useState, useEffect, useMemo, useRef } from "react";
import { Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Popover from "@mui/material/Popover";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import StyleIcon from "@mui/icons-material/Style";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import CardContainer from "../../../../components/Card/CardContainer";
import Button from "../../../../components/Buttons/Button";
import SectionHeader from "./SectionHeader";
import BrandMultilineField from "./BrandMultilineField";
import {
  SECTION_COLORS_TITLE,
  SECTION_COLORS_SUBTITLE,
  SECTION_COLORS_ADD_COLOR,
  BUTTON_COPY,
  SECTION_OFFER_VISUAL_STYLE_LABEL,
} from "../../../../i18n/keysTranslations";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  INPUT_COLOR_BORDER,
  BOX_SHADOW_FIELDS,
} from "../../../../utils/colors";
import { BORDER_RADIUS } from "../../../../utils/constants";

const ColorPicker = ({ value, onChange, onBlur, sxTextField }) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <input
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        style={{
          width: 40,
          height: 40,
          borderRadius: BORDER_RADIUS,
          cursor: "pointer",
          border: "none",
          backgroundColor: "transparent",
          padding: 0,
        }}
      />
      <TextField
        value={value}
        onChange={(e) => onChange(e.target.value)}
        size="small"
        fullWidth
        sx={sxTextField}
      />
    </Box>
  );
};

const ColorPopover = ({
  open,
  anchorEl,
  onClose,
  color,
  onColorChange,
  mode = "add",
  index,
  onRemoveColor,
  onCopyColor,
  copiedIndex,
  onAddColor,
  sxTextField,
  t,
}) => {
  const isEditMode = mode === "edit";
  const colorPickerJustClosedRef = useRef(false);

  const handleColorPickerBlur = () => {
    colorPickerJustClosedRef.current = true;
    setTimeout(() => {
      colorPickerJustClosedRef.current = false;
    }, 150);
  };

  const handleClose = (event, reason) => {
    if (reason === "backdropClick" && colorPickerJustClosedRef.current) {
      return;
    }
    onClose(event, reason);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          width: 208,
          p: 2,
          backgroundColor: "background.paper",
          border: `1px solid ${INPUT_COLOR_BORDER}`,
          boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
          borderRadius: BORDER_RADIUS,
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <ColorPicker
          value={color}
          onChange={onColorChange}
          onBlur={handleColorPickerBlur}
          sxTextField={sxTextField}
        />
        {isEditMode ? (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              size="small"
              color="secondary"
              fullWidth
              onClick={() => onCopyColor(index, color)}
              startIcon={
                copiedIndex === index ? (
                  <CheckIcon sx={{ fontSize: 12 }} />
                ) : (
                  <ContentCopyIcon sx={{ fontSize: 12 }} />
                )
              }
            >
              {t(BUTTON_COPY)}
            </Button>
            <IconButton
              size="small"
              onClick={() => onRemoveColor(index)}
              sx={{
                color: "text.primary",
                "&:hover": {
                  backgroundColor: "action.hover",
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 24 }} />
            </IconButton>
          </Box>
        ) : (
          <Button
            size="small"
            fullWidth
            onClick={onAddColor}
            startIcon={
              <AddIcon sx={{ fontSize: 24, color: "#fff !important" }} />
            }
          >
            {t(SECTION_COLORS_ADD_COLOR)}
          </Button>
        )}
      </Box>
    </Popover>
  );
};

const ColorItem = ({
  color,
  index,
  onColorClick,
  isHovered,
  onMouseEnter,
  onMouseLeave,
  openPopover,
  anchorEl,
  onClosePopover,
  onUpdateColor,
  onRemoveColor,
  onCopyColor,
  copiedIndex,
  sxTextField,
  t,
}) => {
  return (
    <Box
      sx={{
        position: "relative",
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <IconButton
        onClick={(e) => onColorClick(e, index)}
        sx={{
          p: 0,
          "&:hover": {
            transform: "scale(1.05)",
          },
          transition: "transform 0.2s",
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            border: `2px solid ${INPUT_COLOR_BORDER}`,
            boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
            backgroundColor: color,
            position: "relative",
          }}
        />
      </IconButton>
      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          bottom: -18,
          left: "50%",
          transform: "translateX(-50%)",
          fontFamily: "monospace",
          fontSize: "10px",
          color: "text.secondary",
          opacity: isHovered ? 1 : 0,
          transition: "opacity 0.2s",
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}
      >
        {color}
      </Typography>

      <ColorPopover
        open={openPopover}
        anchorEl={anchorEl}
        onClose={onClosePopover}
        color={color}
        onColorChange={(newColor) => onUpdateColor(index, newColor)}
        mode="edit"
        index={index}
        onRemoveColor={onRemoveColor}
        onCopyColor={onCopyColor}
        copiedIndex={copiedIndex}
        sxTextField={sxTextField}
        t={t}
      />
    </Box>
  );
};

const AddColorButton = ({ onClick, sx }) => {
  return (
    <IconButton onClick={onClick} sx={sx}>
      <AddIcon sx={{ fontSize: 20, color: "#fff" }} />
    </IconButton>
  );
};

export const ColorsSection = ({ formMethods, hasError = false }) => {
  const { t } = useTranslationApp();
  const [newColor, setNewColor] = useState("#10B981");
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [editingIndex, setEditingIndex] = useState(null);
  const [addColorAnchorEl, setAddColorAnchorEl] = useState(null);
  const [hoveredColorIndex, setHoveredColorIndex] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedStyle, setEditedStyle] = useState("");

  const businessLookAndFeel = useMemo(
    () => formMethods?.watch?.("businessLookAndFeel"),
    [formMethods]
  );

  useEffect(() => {
    if (!isEditing && formMethods?.control) {
      setEditedStyle(businessLookAndFeel);
    }
  }, [businessLookAndFeel, isEditing, formMethods?.control]);

  if (!formMethods || !formMethods.control) {
    return null;
  }

  const { control, errors, validationRules } = formMethods;

  const open = Boolean(anchorEl);
  const addColorOpen = Boolean(addColorAnchorEl);

  const addColor = (colors, onUpdate) => {
    if (colors.length < 8) {
      onUpdate([...colors, newColor]);
      setNewColor("#10B981");
      setAddColorAnchorEl(null);
    }
  };

  const removeColor = (index, colors, onUpdate) => {
    onUpdate(colors.filter((_, i) => i !== index));
    setAnchorEl(null);
    setEditingIndex(null);
  };

  const updateColor = (index, color, colors, onUpdate) => {
    const updated = [...colors];
    updated[index] = color;
    onUpdate(updated);
  };

  const copyColor = (index, color) => {
    navigator.clipboard.writeText(color);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 1500);
  };

  const onColorClick = (event, index) => {
    if (!isEditing) return;
    setEditingIndex(index);
    setAnchorEl(event.currentTarget);
  };

  const onAddColorClick = (event) => {
    if (!isEditing) return;
    setAddColorAnchorEl(event.currentTarget);
  };

  const onClosePopover = () => {
    setAnchorEl(null);
    setEditingIndex(null);
  };

  const onCloseAddColorPopover = () => {
    setAddColorAnchorEl(null);
  };

  const sxTextField = {
    "& .MuiInputBase-root": {
      backgroundColor: "fields.background",
      borderRadius: BORDER_RADIUS,
      color: "#000 !important",
      border: `1px solid ${INPUT_COLOR_BORDER}`,
      boxShadow: BOX_SHADOW_FIELDS,
      "& input": {
        color: "#000 !important",
        fontFamily: "monospace",
        fontSize: "0.875rem",
      },
    },
  };

  const onSave = () => {
    if (!formMethods?.setValue) return;
    formMethods.setValue("businessLookAndFeel", editedStyle);
    setIsEditing(false);
  };

  const handleEdit = async () => {
    setIsEditing(true);
    // Trigger validation when entering edit mode
    await formMethods.trigger?.(["businessLookAndFeel", "businessColors"]);
  };

  return (
    <CardContainer title="" hasError={hasError}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Header */}
        <SectionHeader
          icon={StyleIcon}
          sxIcon={{ width: 22, height: 22 }}
          title={SECTION_COLORS_TITLE}
          subtitle={SECTION_COLORS_SUBTITLE}
          isEditing={isEditing}
          onEdit={handleEdit}
          onSave={onSave}
        />

        {/* Colors Grid */}
        <Controller
          control={control}
          name="businessColors"
          rules={validationRules.businessColors}
          render={({ field }) => {
            const colors = Array.isArray(field.value) ? field.value : [];
            const onUpdate = (updatedColors) => field.onChange(updatedColors);
            return (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, pb: 2 }}>
                {colors.map((color, index) => (
                  <ColorItem
                    key={index}
                    color={color}
                    index={index}
                    onColorClick={onColorClick}
                    isHovered={hoveredColorIndex === index}
                    onMouseEnter={() => setHoveredColorIndex(index)}
                    onMouseLeave={() => setHoveredColorIndex(null)}
                    openPopover={open && editingIndex === index}
                    anchorEl={anchorEl}
                    onClosePopover={onClosePopover}
                    onUpdateColor={(idx, newColor) =>
                      updateColor(idx, newColor, colors, onUpdate)
                    }
                    onRemoveColor={(idx) => removeColor(idx, colors, onUpdate)}
                    onCopyColor={copyColor}
                    copiedIndex={copiedIndex}
                    sxTextField={sxTextField}
                    t={t}
                  />
                ))}

                {/* Add Color Button */}
                {isEditing && colors.length < 8 && (
                  <>
                    <AddColorButton
                      onClick={onAddColorClick}
                      sx={{
                        width: 56,
                        height: 56,
                        border: `2px dashed ${INPUT_COLOR_BORDER}`,
                        borderRadius: 2,
                        "&:hover": {
                          borderColor: "text.primary",
                          backgroundColor: "action.hover",
                        },
                        transition: "all 0.2s",
                      }}
                    />

                    <ColorPopover
                      open={addColorOpen}
                      anchorEl={addColorAnchorEl}
                      onClose={onCloseAddColorPopover}
                      color={newColor}
                      onColorChange={setNewColor}
                      mode="add"
                      onAddColor={() => addColor(colors, onUpdate)}
                      sxTextField={sxTextField}
                      t={t}
                    />
                  </>
                )}
                {errors?.businessColors?.message && isEditing && (
                  <Typography
                    variant="caption"
                    sx={{ color: "error.main", mt: -1, mb: 1 }}
                  >
                    {errors.businessColors.message}
                  </Typography>
                )}
              </Box>
            );
          }}
        />

        {/* Brand Visual Style */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          <Box sx={{ display: isEditing ? "block" : "none" }}>
            <Typography
              variant="body2"
              sx={{
                color: "text.primary",
                fontSize: "14px",
                display: "block",
              }}
            >
              {t(SECTION_OFFER_VISUAL_STYLE_LABEL)}
            </Typography>
            <BrandMultilineField
              control={control}
              name="businessLookAndFeel"
              value={editedStyle}
              actionsChange={(value) => setEditedStyle(value)}
              rules={validationRules.businessLookAndFeel}
              errors={errors}
            />
          </Box>
          {!isEditing && (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                lineHeight: 1.6,
                fontSize: "0.875rem",
              }}
            >
              {businessLookAndFeel}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContainer>
  );
};

export default ColorsSection;
